import React, { useState } from "react";
import { Box, Drawer, IconButton, List, NoSsr, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import useIsScrolled from "../../hooks/useIsScrolled";
import { StaticImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import ThemeBtn from "../components/ThemeBtn";
import ContainerBlock from "../components/ContainerBlock";
import CloseIcon from "@mui/icons-material/Close";

const NAV_LINKS = [
	{ to: "/case-studies/", label: "Case Studies", title: "Case Studies" },
	{ to: "/careers/", label: "Careers", title: "Careers" },
	{ to: "/events/", label: "Events", title: "Events" },
	{ to: "/contact-us/", label: "Contact Us", title: "Contact Us" },
	{ to: "/blogs/", label: "Blogs", title: "Blogs" },
];

const Header = () => {

	const [drawerOpen, setDrawerOpen] = useState(false);
	const isScrolled = useIsScrolled();
	const theme = useTheme();

	const checkActive = (to: any, location: any) => {
		const path = location.pathname;
		if (to === "/") return path === "/";
		return path.startsWith(to);
	};

	const getCurrentYear = (): number => new Date().getFullYear();


	return (
		<Box
			component="header"
			sx={{
				maxWidth: "100dvw",
				position: "fixed",
				top: 0,
				left: 0,
				width: "100%",
				zIndex: 100,
				backdropFilter: isScrolled ? "blur(4px)" : "none",
				backgroundColor: isScrolled ? "#FFFFFFCC" : "transparent",
				transition: "all 0.3s ease",
			}}
		>
			<ContainerBlock
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					paddingY: { xs: "14px", md: "22px" },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{/* Mobile Menu Icon */}
					<Box sx={{ display: { xs: "block", md: "none" } }}>
						<IconButton
							onClick={() => setDrawerOpen(true)}
							aria-label="menu btn"
						>
							<MenuIcon sx={{ fontSize: 28 }} />
						</IconButton>
					</Box>

					{/* Logo */}
					<Link to="/" style={{ display: "inline-block" }} title="Home">
						<Box
							sx={{
								width: { xs: 120, md: 180 },
								display: "flex",
								alignItems: "center",
							}}
						>
							<StaticImage
								src="../../../static/logos/logo-dt.png"
								placeholder="blurred"
								loading="lazy"
								alt="Xtellig Logo"
								imgStyle={{ objectFit: "contain" }}
								style={{ width: "100%", height: "auto" }}
							/>
						</Box>
					</Link>
				</Box>

				{/* Center (Desktop Navigation Only) */}
				<Box
					component="nav"
					aria-label="Main navigation"
					sx={{
						display: { xs: "none", md: "flex" },
						gap: "24px",
						alignItems: "center",
					}}
				>
					{NAV_LINKS.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							title={item.title}
							// partiallyActive is still good to keep for accessibility
							partiallyActive={item.to !== "/"}
							getProps={({ location }) => {
								const isActive = checkActive(item.to, location);

								return {
									style: {
										textDecoration: "none",
										padding: "8px 12px",
										fontSize: "15px",
										fontWeight: 600,
										transition: "color 0.3s ease",
										color: isActive ? theme.palette.primary.main : "#2B3557",
									},
								};
							}}
						>
							{item.label}
						</Link>
					))}
				</Box>

				<Box sx={{ display: "flex", alignItems: "center" }}>
					<ThemeBtn
						label={"Let’s talk"}
						link={"https://cal.com/jarachanthan/xtellig?duration=30"}
					/>
				</Box>
			</ContainerBlock>

			{/* Mobile Drawer */}
			<Drawer
				anchor="left"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			>
				<Box
					sx={{
						width: "100vw",
						height: "100vh",
						p: "36px 40px",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box
						display={"flex"}
						flexDirection={"row"}
						justifyContent={"space-between"}
						onClick={() => setDrawerOpen(false)}
					>
						{/* Logo */}
						<Link to="/" style={{ display: "inline-block" }} title="Home">
							<Box
								sx={{
									width: { xs: 120, md: 180 },
									display: "flex",
									alignItems: "center",
								}}
							>
								<StaticImage
									src="../../../static/logos/logo-dt.png"
									placeholder="blurred"
									loading="lazy"
									alt="Xtellig Logo"
									imgStyle={{ objectFit: "contain" }}
									style={{ width: "100%", height: "auto" }}
								/>
							</Box>
						</Link>
						<CloseIcon
							sx={{ fontSize: "17px" }}
							onClick={() => setDrawerOpen(false)}
						/>
					</Box>
					<Box
						component="nav"
						aria-label="Mobile navigation"
					><List
						sx={{
							p: 0,
							mt: "80px",
							gap: "45px",
							display: "flex",
							flexDirection: "column",
						}}
					>
							{NAV_LINKS.map((item) => (
								<Link
									key={item.to}
									to={item.to}
									onClick={() => setDrawerOpen(false)}
									partiallyActive={item.to !== "/"}
									getProps={({ location }) => {
										const isActive = checkActive(item.to, location);

										return {
											style: {
												textDecoration: "none",
												fontSize: "25px",
												fontWeight: 400,
												color: isActive ? theme.palette.primary.main : "#282C4B",
											},
										};
									}}
								>
									{item.label}
								</Link>
							))}
						</List></Box>

					<Box
						sx={{
							mt: "auto",
							display: "flex",
							justifyContent: "center",
							fontSize: "14px",
							color: "#8A8FA3",
						}}
					>
						© {getCurrentYear()} Xtellig Pvt Ltd. All rights reserved.
					</Box>
				</Box>
			</Drawer>
		</Box>
	);
};

export default Header;
